// Client-safe helpers and types for reading Navigation menu display options

import { JsonValue } from "@prisma/client/runtime/library";



export interface webNavigationOptionsType {
    customLevel2BgColor: string;
    customLevel3BgColor: string;
    level2BgColorKey: string; // key for accessing color from colorStyle
    level3BgColorKey: string; // key for accessing color from colorStyle
    customColorStyle: string;
    level2PaddingPreset: string; // COMPACT | COMFORTABLE | CUSTOM
    level3PaddingPreset: string; // COMPACT | COMFORTABLE | CUSTOM
    level2PaddingX: number;
    level2PaddingY: number;
    level3PaddingX: number;
    level3PaddingY: number;
    level2PaddingLeft?: number;
    level2PaddingRight?: number;
    level2PaddingTop?: number;
    level2PaddingBottom?: number;
    level3PaddingLeft?: number;
    level3PaddingRight?: number;
    level3PaddingTop?: number;
    level3PaddingBottom?: number;
    enableLevel2Divider: boolean;
    enableLevel3Divider: boolean;
}
export interface GeneralDisplayOptions {
    flexDirection?: "column" | "row"; // layout for level-2 when no level-3
    showSectionTitle?: boolean; // show heading inside level-3 panel
    sectionTitleAttribute? : Array<JsonValue>; // attribute object for section title
}

export interface CollectionDisplayOptions {
    showProductsInCollection?: boolean;
    showTextOnly?: boolean; // legacy
    showTextAndImages?: boolean; // legacy
    collectionGridColumns?: 2 | 3 | 4 | 5 | 6;
    limitProductsInCollection?: number; // 0 / undefined => unlimited
    productBorderStyle?: "circle" | "rectangle" | "plain";
    contentDisplay?: string; // e.g. TEXT_ONLY / TEXT_AND_IMAGE
    textStyle?: string; // e.g. regular / bold
}

export interface ProductDisplayOptions {
    showTextOnly?: boolean;
    showTextAndImages?: boolean;
    productBorderStyle?: "circle" | "rectangle" | "plain";
    contentDisplay?: string;
    textStyle?: string;
}

// Safe readers that look up JSON fields on the NavigationMenu object directly.
// They do not import any server-only modules, so they can be used in client components.
export function readWebNavigationOptions(webNav: any): webNavigationOptionsType {
    // Accept either flattened fields or nested under webNavigationOption
    const raw = (webNav?.webNavigationOption || webNav || {}) as any;
    const preset2 = (raw.level2PaddingPreset || "COMFORTABLE").toString().toUpperCase();
    const preset3 = (raw.level3PaddingPreset || "COMFORTABLE").toString().toUpperCase();
    const validPresets = ["COMPACT", "COMFORTABLE", "CUSTOM"];
    return {
        customLevel2BgColor: raw.customLevel2BgColor ?? "#FFFFFF",
        customLevel3BgColor: raw.customLevel3BgColor ?? "#FFFFFF",
        level2BgColorKey: raw.level2BgColorKey ?? "",
        level3BgColorKey: raw.level3BgColorKey ?? "",
        customColorStyle: raw.customColorStyle ?? "#FFFFFF",
        level2PaddingPreset: validPresets.includes(preset2) ? preset2 : "COMFORTABLE",
        level3PaddingPreset: validPresets.includes(preset3) ? preset3 : "COMFORTABLE",
        level2PaddingX: typeof raw.level2PaddingX === "number" ? raw.level2PaddingX : 12,
        level2PaddingY: typeof raw.level2PaddingY === "number" ? raw.level2PaddingY : 8,
        level3PaddingX: typeof raw.level3PaddingX === "number" ? raw.level3PaddingX : 10,
        level3PaddingY: typeof raw.level3PaddingY === "number" ? raw.level3PaddingY : 6,
        level2PaddingLeft: typeof raw.level2PaddingLeft === "number" ? raw.level2PaddingLeft : undefined,
        level2PaddingRight: typeof raw.level2PaddingRight === "number" ? raw.level2PaddingRight : undefined,
        level2PaddingTop: typeof raw.level2PaddingTop === "number" ? raw.level2PaddingTop : undefined,
        level2PaddingBottom: typeof raw.level2PaddingBottom === "number" ? raw.level2PaddingBottom : undefined,
        level3PaddingLeft: typeof raw.level3PaddingLeft === "number" ? raw.level3PaddingLeft : undefined,
        level3PaddingRight: typeof raw.level3PaddingRight === "number" ? raw.level3PaddingRight : undefined,
        level3PaddingTop: typeof raw.level3PaddingTop === "number" ? raw.level3PaddingTop : undefined,
        level3PaddingBottom: typeof raw.level3PaddingBottom === "number" ? raw.level3PaddingBottom : undefined,
        enableLevel2Divider: !!raw.enableLevel2Divider,
        enableLevel3Divider: !!raw.enableLevel3Divider,
    };
}

export function readGeneralOptions(menu: any): GeneralDisplayOptions {
    const raw = (menu?.menuShowOptionGeneral ?? {}) as Partial<GeneralDisplayOptions>;
    const styleRaw = (raw as any)?.sectionTitleTextStyle;
    let sectionTitleTextStyle: string | undefined;
    if (typeof styleRaw === "string") {
        const lowered = styleRaw.toLowerCase();
        if (["regular", "light", "bold"].includes(lowered)) sectionTitleTextStyle = lowered;
        else if (["REGULAR", "LIGHT", "BOLD"].includes(styleRaw)) sectionTitleTextStyle = lowered.toLowerCase();
        else sectionTitleTextStyle = "light"; // fallback default
    } else {
        sectionTitleTextStyle = "light"; // default when absent
    }

    return {
        flexDirection: raw.flexDirection === "row" ? "row" : "column",
        showSectionTitle: raw.showSectionTitle !== false,
        sectionTitleAttribute: Array.isArray((raw as any).sectionTitleAttribute) ? (raw as any).sectionTitleAttribute : undefined,
    };
}

export function readCollectionOptions(menu: any): CollectionDisplayOptions {
    const raw = (menu?.menuShowOptionCollection ??
        menu?.menuShowOptionForCollection ??
        {}) as Partial<CollectionDisplayOptions> & {
        contentDisplay?: string;
        textStyle?: string;
    };
    const displayMode = raw.contentDisplay;
    // Derive legacy booleans if only contentDisplay is provided
    let showTextOnly = raw.showTextOnly;
    let showTextAndImages = raw.showTextAndImages;
    if (displayMode && showTextOnly === undefined && showTextAndImages === undefined) {
        if (displayMode === "TEXT_ONLY") {
            showTextOnly = true;
            showTextAndImages = false;
        } else if (displayMode === "TEXT_AND_IMAGE") {
            showTextOnly = false;
            showTextAndImages = true;
        }
    }
    // If contentDisplay missing but legacy flags present, synthesize it so downstream logic is simpler
    let normalizedContentDisplay = displayMode;
    if (!normalizedContentDisplay) {
        if (showTextOnly === true && showTextAndImages !== true) normalizedContentDisplay = "TEXT_ONLY";
        else if (showTextAndImages === true && showTextOnly !== true) normalizedContentDisplay = "TEXT_AND_IMAGE";
    }

    // TEMPORARY OVERRIDE: Force all collection display to TEXT_AND_IMAGE.
    // This keeps future flexibility: set ALWAYS_FORCE_COLLECTION_IMAGES=false to restore dynamic behavior.
    const ALWAYS_FORCE_COLLECTION_IMAGES = true;
    if (ALWAYS_FORCE_COLLECTION_IMAGES) {
        normalizedContentDisplay = "TEXT_AND_IMAGE";
        showTextOnly = false;
        showTextAndImages = true;
    }
    const textStyleRaw = raw.textStyle;
    // console.log("text style : ", textStyleRaw, "of menu ", menu.attributes[0].displayName);
    let normTextStyle: string | undefined;
    if (typeof textStyleRaw === "string") {
        const lowered = textStyleRaw.toLowerCase();
        if (["regular", "bold", "light"].includes(lowered)) normTextStyle = lowered;
        else if (["REGULAR", "BOLD", "LIGHT"].includes(textStyleRaw)) normTextStyle = lowered.toLowerCase();
    }
    return {
        showProductsInCollection: !!raw.showProductsInCollection,
        showTextOnly: showTextOnly ?? (!raw.showProductsInCollection && !showTextAndImages),
        showTextAndImages: showTextAndImages ?? false,
        collectionGridColumns: [2, 3, 4, 5, 6].includes(raw.collectionGridColumns as any)
            ? (raw.collectionGridColumns as any)
            : 4,
        limitProductsInCollection:
            typeof raw.limitProductsInCollection === "number" && raw.limitProductsInCollection > 0
                ? raw.limitProductsInCollection
                : undefined,
        productBorderStyle: ["circle", "rectangle", "plain"].includes(raw.productBorderStyle as any)
            ? (raw.productBorderStyle as any)
            : "rectangle",
        contentDisplay: normalizedContentDisplay,
        textStyle: normTextStyle || "regular",
    };
}

export function readProductOptions(menu: any): ProductDisplayOptions {
    const raw = (menu?.menuShowOptionProduct ??
        menu?.menuShowOptionForProducts ??
        {}) as Partial<ProductDisplayOptions> & {
        contentDisplay?: string;
        textStyle?: string;
    };
    const displayMode = raw.contentDisplay;
    let showTextOnly = raw.showTextOnly;
    let showTextAndImages = raw.showTextAndImages;
    if (displayMode && showTextOnly === undefined && showTextAndImages === undefined) {
        if (displayMode === "TEXT_ONLY") {
            showTextOnly = true;
            showTextAndImages = false;
        } else if (displayMode === "TEXT_AND_IMAGE") {
            showTextOnly = false;
            showTextAndImages = true;
        }
    }
    let normalizedContentDisplay = displayMode;
    if (!normalizedContentDisplay) {
        if (showTextOnly === true && showTextAndImages !== true) normalizedContentDisplay = "TEXT_ONLY";
        else if (showTextAndImages === true && showTextOnly !== true) normalizedContentDisplay = "TEXT_AND_IMAGE";
    }
    const textStyleRaw = raw.textStyle;
    let normTextStyle: string | undefined;
    if (typeof textStyleRaw === "string") {
        const lowered = textStyleRaw.toLowerCase();
        if (["regular", "bold", "light"].includes(lowered)) normTextStyle = lowered;
        else if (["REGULAR", "BOLD", "LIGHT"].includes(textStyleRaw)) normTextStyle = lowered.toLowerCase();
    }
    return {
        showTextOnly: showTextOnly ?? true,
        showTextAndImages: showTextAndImages ?? false,
        productBorderStyle: ["circle", "rectangle", "plain"].includes(raw.productBorderStyle as any)
            ? (raw.productBorderStyle as any)
            : "rectangle",
        contentDisplay: normalizedContentDisplay,
        textStyle: normTextStyle || "regular",
    };
}
